/**
 * The living background («Orbes»): soft circles of light travel slowly behind a screen. Each is one plain gradient,
 * painted once and moved by transform alone, in the stylesheet: no blur filter and no script while it runs. The script
 * here only tells the layer how large its host is, pauses it when the tab is hidden or the layer is off screen, and
 * lets one faint light follow the pointer on a lazy spring (absent on touch). Under reduced motion it is a still picture.
 *
 * Use: the first child of the screen's scrolling element; everything after it must be positioned (`position: relative`)
 * to paint above it. One per screen root.
 */

import { lazy, Suspense, useEffect, useRef } from 'react';

import { useMediaQuery } from '../adaptive';
import { useReducedMotion } from '../motion';
import { useLivingTone } from './tone';
import './living.scss';

const PointerLight = lazy(() => import('./living-background-pointer').then((module) => ({ default: module.PointerLight })));

/** «Solar»: five light oranges, from coral to gold. «Noche»: three very slow, faint embers (the night screens: Tarjetas, the Constructor). */
type LivingPalette = 'solar' | 'noche';

const ORBS: Readonly<Record<LivingPalette, readonly number[]>> = { solar: [1, 2, 3, 4, 5], noche: [1, 2, 3] };
const FINE_POINTER = '(hover: hover) and (pointer: fine)';

export interface LivingBackgroundProps {
  /** Defaults to the tone of the screen: «solar» by day, «noche» inside a night scope. */
  readonly palette?: LivingPalette;
  /** The light that follows the pointer; on by default, never on touch. */
  readonly pointer?: boolean;
  readonly className?: string;
}

export function LivingBackground({ palette, pointer = true, className }: LivingBackgroundProps) {
  const tone = useLivingTone();
  const colours = palette ?? (tone === 'noche' ? 'noche' : 'solar');
  const still = useReducedMotion();
  const follows = useMediaQuery(FINE_POINTER) && pointer && !still;
  const layer = useRef<HTMLDivElement>(null);
  const frame = useRef<HTMLDivElement>(null);

  // How large the host is: the loops are written in fractions of it.
  useEffect(() => {
    const host = layer.current?.parentElement;
    const box = frame.current;
    if (!host || !box) return undefined;
    const size = () => {
      box.style.setProperty('--w', `${host.clientWidth}px`);
      box.style.setProperty('--h', `${Math.min(host.clientHeight, window.innerHeight)}px`);
    };
    size();
    window.addEventListener('resize', size);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(size);
    observer?.observe(host);
    return () => { window.removeEventListener('resize', size); observer?.disconnect(); };
  }, []);

  // Nothing runs while nobody can see it.
  useEffect(() => {
    const node = layer.current;
    const box = frame.current;
    if (!node || !box || still) return undefined;
    let offScreen = false;
    const mark = () => node.toggleAttribute('data-away', document.hidden || offScreen);
    mark();
    document.addEventListener('visibilitychange', mark);
    const observer = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver((entries) => {
      offScreen = !(entries[entries.length - 1]?.isIntersecting ?? true);
      mark();
    });
    observer?.observe(box);
    return () => { document.removeEventListener('visibilitychange', mark); observer?.disconnect(); node.removeAttribute('data-away'); };
  }, [still]);

  return (
    <div aria-hidden="true" className={className === undefined ? 'lv-live' : `lv-live ${className}`} data-palette={colours} ref={layer}>
      <div className="lv-live__in" ref={frame}>
        {ORBS[colours].map((orb) => <i className={`lv-orb lv-orb--${orb}`} key={orb} />)}
        {/* The light and its spring load apart from the Panel's entry; until they land there is no pointer to follow. */}
        {follows ? <Suspense fallback={null}><PointerLight layer={layer} /></Suspense> : null}
      </div>
    </div>
  );
}
